// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Profile Panel Component (Tabbed Interface)
// ═══════════════════════════════════════════════════════════════════════════
// Contains Stats, Settings, Saved Verses, and Journal entries in a tabbed layout
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import * as storage from '../services/storage.js';
import { logThemeChange, logSoakTimeChange, logSettingsOpen, getUserStats } from '../services/analytics.js';
import { THEMES } from '../config/themes.js';
import { getCurrentUser, logOut, signInWithGoogle } from '../services/auth.js';
import { showJournalEditor } from './JournalEditor.js';
import { isInstallable, triggerInstall, getInstallStatus } from './InstallPrompt.js';
import { downloadCSV, downloadMarkdown, shareCSV, shareMarkdown } from '../services/dataExport.js';
import { escapeHtml, escapeUrl } from '../utils/escape.js';
import { showToast } from '../utils/toast.js';

let currentOverlay = null;
let currentTab = 'stats';
let onViewVerse = null;
let escKeyHandler = null;

// Registered by main.js so tapping a saved verse navigates to it (#42).
export function setProfileViewVerseCallback(callback) {
  onViewVerse = callback;
}

// Shared sign-in flow that surfaces failures instead of silently reopening
// the signed-out view (#55). Only re-renders the panel on success.
async function handleProfileSignIn(tab) {
  const result = await signInWithGoogle();
  if (result && result.success === false) {
    showToast(result.error || 'Sign-in failed. Please try again.');
    return;
  }
  // On success (or pending redirect) refresh the panel into the chosen tab.
  hideProfile();
  showProfile(tab);
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT OPTIONS MODAL
// ─────────────────────────────────────────────────────────────────────────

function showExportOptions() {
  const modal = document.createElement('div');
  modal.className = 'export-modal-overlay';
  modal.innerHTML = `
    <div class="export-modal">
      <div class="export-modal-header">
        <h3>Export My Data</h3>
        <button class="export-modal-close" id="export-close">${icons.x}</button>
      </div>
      <div class="export-modal-content">
        <p class="export-modal-desc">Choose a format and action:</p>
        
        <div class="export-format-section">
          <h4>📄 CSV (Spreadsheet)</h4>
          <p class="export-format-desc">Best for importing into Excel or Google Sheets</p>
          <div class="export-actions">
            <button class="export-action-btn" id="download-csv">
              ${icons.share}
              Download
            </button>
            <button class="export-action-btn" id="share-csv">
              ${icons.share}
              Share
            </button>
          </div>
        </div>
        
        <div class="export-format-section">
          <h4>📝 Markdown</h4>
          <p class="export-format-desc">Beautiful, readable format with all your notes</p>
          <div class="export-actions">
            <button class="export-action-btn" id="download-md">
              ${icons.share}
              Download
            </button>
            <button class="export-action-btn" id="share-md">
              ${icons.share}
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  requestAnimationFrame(() => modal.classList.add('active'));

  // Close handlers
  const closeModal = () => {
    modal.classList.remove('active');
    setTimeout(() => modal.remove(), 300);
  };

  modal.querySelector('#export-close').addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  // Export handlers
  modal.querySelector('#download-csv').addEventListener('click', async () => {
    await downloadCSV();
    closeModal();
  });

  modal.querySelector('#share-csv').addEventListener('click', async () => {
    await shareCSV();
    closeModal();
  });

  modal.querySelector('#download-md').addEventListener('click', async () => {
    await downloadMarkdown();
    closeModal();
  });

  modal.querySelector('#share-md').addEventListener('click', async () => {
    await shareMarkdown();
    closeModal();
  });
}

export function showProfile(initialTab = 'settings') {
  if (currentOverlay) {
    hideProfile();
  }

  currentTab = initialTab;
  logSettingsOpen();

  const overlay = document.createElement('div');
  overlay.className = 'profile-overlay';
  overlay.id = 'profile-overlay';

  const user = getCurrentUser();
  const currentTheme = storage.getTheme();
  const currentSoakTime = storage.getSoakTime();
  const favorites = storage.getFavorites();
  const journalEntries = storage.getJournalEntries();

  overlay.innerHTML = `
    <div class="profile-panel">
      <div class="profile-header">
        <h2 class="profile-title">Profile</h2>
        <button class="profile-close-btn" id="profile-close" aria-label="Close">
          ${icons.x}
        </button>
      </div>
      
      <div class="profile-tabs">
        <button class="profile-tab ${currentTab === 'stats' ? 'active' : ''}" data-tab="stats">
          ${icons.trending}
          Stats
        </button>
        <button class="profile-tab ${currentTab === 'settings' ? 'active' : ''}" data-tab="settings">
          ${icons.settings}
          Settings
        </button>
        <button class="profile-tab ${currentTab === 'saved' ? 'active' : ''}" data-tab="saved">
          ${icons.saved}
          Saved
        </button>
        <button class="profile-tab ${currentTab === 'journal' ? 'active' : ''}" data-tab="journal">
          ${icons.journal}
          Journal
        </button>
      </div>
      
      <div class="profile-content" id="profile-content">
        <!-- Content will be injected based on active tab -->
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  currentOverlay = overlay;

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  // Setup event handlers
  setupProfileHandlers(overlay, user, currentTheme, currentSoakTime);

  // Render initial tab content
  renderTabContent(currentTab, favorites, journalEntries, currentTheme, currentSoakTime, user);
}

function setupProfileHandlers(overlay, user, currentTheme, currentSoakTime) {
  // Close button
  overlay.querySelector('#profile-close').addEventListener('click', hideProfile);

  // Click outside to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      hideProfile();
    }
  });

  // ESC key to close. Stored at module scope so hideProfile() can always
  // detach it, no matter how the panel was dismissed (#94).
  escKeyHandler = (e) => {
    if (e.key === 'Escape') hideProfile();
  };
  document.addEventListener('keydown', escKeyHandler);

  // Tab switching
  overlay.querySelectorAll('.profile-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const tabName = tab.dataset.tab;
      switchTab(tabName);
    });
  });
}

async function switchTab(tabName) {
  currentTab = tabName;

  // Update tab active states
  document.querySelectorAll('.profile-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabName);
  });

  // Re-render content
  const favorites = storage.getFavorites();
  const journalEntries = storage.getJournalEntries();
  const currentTheme = storage.getTheme();
  const currentSoakTime = storage.getSoakTime();
  const user = getCurrentUser();

  await renderTabContent(tabName, favorites, journalEntries, currentTheme, currentSoakTime, user);
}

async function renderTabContent(tabName, favorites, journalEntries, currentTheme, currentSoakTime, user) {
  const container = document.getElementById('profile-content');
  if (!container) {
    console.error('Profile content container not found');
    return;
  }

  try {
    switch (tabName) {
      case 'stats':
        await renderStatsTab(container, favorites, journalEntries);
        break;
      case 'settings':
        renderSettingsTab(container, currentTheme, currentSoakTime, user);
        break;
      case 'saved':
        renderSavedTab(container, favorites);
        break;
      case 'journal':
        renderJournalTab(container, journalEntries);
        break;
      default:
        container.innerHTML = '<p class="profile-empty-text">Select a tab</p>';
    }
  } catch (error) {
    console.error('Error rendering tab:', tabName, error);
    container.innerHTML = `
      <div class="profile-empty-state">
        <p class="profile-empty-text">Something went wrong. Please try again.</p>
        <p class="profile-empty-text" style="font-size: 12px; opacity: 0.6;">${error.message}</p>
      </div>
    `;
  }
}

async function renderStatsTab(container, favorites, journalEntries) {
  const user = getCurrentUser();

  // Show sign-in prompt if not authenticated
  if (!user) {
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.trending}</span>
        <p class="profile-empty-text">Sign in to view your stats</p>
        <p class="profile-empty-hint">Track your journey, streaks, and engagement</p>
        <button class="profile-signin-btn" id="stats-signin-btn">Sign in with Google</button>
      </div>
    `;
    container.querySelector('#stats-signin-btn')?.addEventListener('click', () => handleProfileSignIn('stats'));
    return;
  }
  // Show loading state first
  container.innerHTML = `
    <div class="stats-loading">
      <div class="stats-loading-spinner"></div>
      <p>Loading your journey...</p>
    </div>
  `;

  try {
    const stats = await getUserStats();

    // Format time nicely
    const formatTime = (ms) => {
      if (ms < 60000) return `${Math.round(ms / 1000)}s`;
      if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
      const hours = Math.floor(ms / 3600000);
      const mins = Math.round((ms % 3600000) / 60000);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    container.innerHTML = `
      <div class="stats-container">
        <div class="stats-highlight">
          <div class="stats-streak">
            <span class="stats-streak-number">${stats.currentStreak}</span>
            <span class="stats-streak-label">Day Streak 🔥</span>
          </div>
          ${stats.longestStreak > stats.currentStreak ? `
            <span class="stats-streak-best">Best: ${stats.longestStreak} days</span>
          ` : ''}
        </div>
        
        <div class="stats-grid">
          <div class="stats-card">
            <span class="stats-card-icon">${icons.eye}</span>
            <span class="stats-card-value">${stats.totalVersesViewed}</span>
            <span class="stats-card-label">Verses Viewed</span>
          </div>
          <div class="stats-card">
            <span class="stats-card-icon">${icons.heart}</span>
            <span class="stats-card-value">${favorites.length}</span>
            <span class="stats-card-label">Verses Saved</span>
          </div>
          <div class="stats-card">
            <span class="stats-card-icon">${icons.clock}</span>
            <span class="stats-card-value">${formatTime(stats.totalTimeSpent)}</span>
            <span class="stats-card-label">Total Time</span>
          </div>
          <div class="stats-card">
            <span class="stats-card-icon">${icons.journal}</span>
            <span class="stats-card-value">${journalEntries.length}</span>
            <span class="stats-card-label">Journal Entries</span>
          </div>
        </div>
        
        <div class="stats-journey">
          <h3 class="stats-journey-title">Your Journey</h3>
          <p class="stats-journey-text">
            ${stats.daysSinceStart > 0
        ? `You've been on this journey for <strong>${stats.daysSinceStart} day${stats.daysSinceStart !== 1 ? 's' : ''}</strong>.`
        : 'Your journey begins today!'
      }
            ${stats.avgTimePerDay > 0
        ? `You spend about <strong>${formatTime(stats.avgTimePerDay)}</strong> with Scripture each day.`
        : ''
      }
          </p>
        </div>
        
        <div class="stats-encouragement">
          ${getEncouragement(stats, favorites.length, journalEntries.length)}
        </div>
      </div>
    `;
  } catch (error) {
    console.error('Error loading stats:', error);
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.trending}</span>
        <p class="profile-empty-text">Keep exploring!</p>
        <p class="profile-empty-hint">Your stats will appear here as you use the app</p>
      </div>
    `;
  }
}

function getEncouragement(stats, savedCount, journalCount) {
  // Personalized encouragement based on user's engagement
  if (stats.currentStreak >= 7) {
    return `<p class="encouragement-text">🌟 Amazing! A week-long streak shows real dedication to Scripture.</p>`;
  } else if (stats.currentStreak >= 3) {
    return `<p class="encouragement-text">🔥 You're building a beautiful habit. Keep going!</p>`;
  } else if (journalCount >= 5) {
    return `<p class="encouragement-text">✍️ Your reflections are treasures. Keep journaling!</p>`;
  } else if (savedCount >= 10) {
    return `<p class="encouragement-text">💝 You've collected many verses. May they bear fruit in your life.</p>`;
  } else if (stats.totalVersesViewed >= 50) {
    return `<p class="encouragement-text">📖 ${stats.totalVersesViewed} verses explored! Your heart is hungry for truth.</p>`;
  } else {
    return `<p class="encouragement-text">🌱 Every journey starts with a single step. Welcome!</p>`;
  }
}


function renderSettingsTab(container, currentTheme, currentSoakTime, user) {
  container.innerHTML = `
    <div class="settings-section">
      <h3 class="settings-section-title">Soak Time</h3>
      <p class="settings-section-desc">How long to dwell on each verse</p>
      <div class="soak-time-options">
        <button class="soak-time-btn ${currentSoakTime === 10 ? 'active' : ''}" data-soak="10">10s</button>
        <button class="soak-time-btn ${currentSoakTime === 30 ? 'active' : ''}" data-soak="30">30s</button>
        <button class="soak-time-btn ${currentSoakTime === 60 ? 'active' : ''}" data-soak="60">60s</button>
      </div>
    </div>
    
    <div class="settings-section">
      <h3 class="settings-section-title">Appearance</h3>
      <div class="theme-options">
        ${Object.entries(THEMES).map(([key, theme]) => `
          <button class="theme-btn ${currentTheme === key ? 'active' : ''}" data-theme="${key}">
            <span class="theme-preview" data-theme="${key}"></span>
            <span class="theme-name">${theme.name}</span>
          </button>
        `).join('')}
      </div>
    </div>
    
    <div class="settings-section">
      <h3 class="settings-section-title">Account</h3>
      ${user ? `
        <div class="settings-user-info">
          <div class="settings-user-avatar">
            ${user.photoURL ? `<img src="${escapeUrl(user.photoURL)}" alt="Profile" referrerpolicy="no-referrer">` : icons.user}
          </div>
          <div class="settings-user-details">
            <span class="settings-user-name">${escapeHtml(user.displayName || 'User')}</span>
            <span class="settings-user-email">${escapeHtml(user.email || '')}</span>
          </div>
        </div>
        <div class="settings-account-actions">
          <button class="settings-signout-btn" id="signout-btn">Sign Out</button>
        </div>
      ` : `
        <button class="settings-signin-btn" id="signin-btn">Sign In with Google</button>
      `}
    </div>
    
    <div class="settings-section">
      <h3 class="settings-section-title">App</h3>
      <div class="settings-app-actions">
        ${getInstallStatus() === 'installable' ? `
          <button class="settings-action-btn install-app-btn" id="install-app-btn">
            ${icons.phone}
            Install App
          </button>
        ` : getInstallStatus() === 'installed' ? `
          <div class="settings-installed-badge">
            ✓ App Installed
          </div>
        ` : ''}
        
        ${user ? `
        <button class="settings-action-btn export-btn" id="export-data-btn">
          ${icons.share}
          Export My Data
        </button>
        ` : ''}
      </div>
    </div>
  `;

  // Theme handlers
  container.querySelectorAll('.theme-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const theme = btn.dataset.theme;
      const oldTheme = storage.getTheme();
      storage.setTheme(theme);
      applyTheme(theme);
      logThemeChange(oldTheme, theme);
      container.querySelectorAll('.theme-btn').forEach(b => b.classList.toggle('active', b.dataset.theme === theme));
    });
  });

  // Soak time handlers
  container.querySelectorAll('.soak-time-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const soak = parseInt(btn.dataset.soak);
      const oldSoak = storage.getSoakTime();
      storage.setSoakTime(soak);
      logSoakTimeChange(oldSoak, soak);
      container.querySelectorAll('.soak-time-btn').forEach(b => b.classList.toggle('active', parseInt(b.dataset.soak) === soak));
    });
  });

  // Sign in/out handlers
  const signinBtn = container.querySelector('#signin-btn');
  const signoutBtn = container.querySelector('#signout-btn');

  if (signinBtn) {
    signinBtn.addEventListener('click', () => handleProfileSignIn('settings'));
  }

  if (signoutBtn) {
    signoutBtn.addEventListener('click', async () => {
      await logOut();
      hideProfile();
      showProfile('settings');
    });
  }

  // Install app handler
  const installBtn = container.querySelector('#install-app-btn');
  if (installBtn) {
    installBtn.addEventListener('click', async () => {
      const success = await triggerInstall();
      if (success) {
        hideProfile();
        showProfile('settings'); // Refresh to show installed state
      }
    });
  }

  // Export data handler
  const exportBtn = container.querySelector('#export-data-btn');
  if (exportBtn) {
    exportBtn.addEventListener('click', () => {
      showExportOptions();
    });
  }
}

function renderSavedTab(container, favorites) {
  const user = getCurrentUser();

  // Show sign-in prompt if not authenticated
  if (!user) {
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.heart}</span>
        <p class="profile-empty-text">Sign in to save verses</p>
        <p class="profile-empty-hint">Your saved verses will sync across devices</p>
        <button class="profile-signin-btn" id="saved-signin-btn">Sign in with Google</button>
      </div>
    `;
    container.querySelector('#saved-signin-btn')?.addEventListener('click', () => handleProfileSignIn('saved'));
    return;
  }

  if (favorites.length === 0) {
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.heart}</span>
        <p class="profile-empty-text">No saved verses yet</p>
        <p class="profile-empty-hint">Tap the heart icon on any verse to save it</p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="saved-list">
      ${favorites.map(fav => `
        <div class="saved-item" data-ref="${escapeHtml(fav.reference)}">
          <div class="saved-item-header">
            <span class="saved-item-ref">${escapeHtml(fav.reference)}</span>
            <span class="saved-item-date">${escapeHtml(formatDate(fav.savedAt))}</span>
          </div>
          <p class="saved-item-text">${escapeHtml(truncateText(fav.text, 100))}</p>
        </div>
      `).join('')}
    </div>
  `;

  // Navigate to the verse when a saved item is tapped (#42)
  container.querySelectorAll('.saved-item').forEach(item => {
    item.addEventListener('click', () => {
      const ref = item.dataset.ref;
      const fav = favorites.find(f => f.reference === ref);
      hideProfile();
      if (fav && onViewVerse) {
        onViewVerse({ reference: fav.reference, text: fav.text });
      }
    });
  });
}

function renderJournalTab(container, journalEntries) {
  const user = getCurrentUser();

  // Show sign-in prompt if not authenticated
  if (!user) {
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.journal}</span>
        <p class="profile-empty-text">Sign in to write journal entries</p>
        <p class="profile-empty-hint">Your reflections will sync across devices</p>
        <button class="profile-signin-btn" id="journal-signin-btn">Sign in with Google</button>
      </div>
    `;
    container.querySelector('#journal-signin-btn')?.addEventListener('click', () => handleProfileSignIn('journal'));
    return;
  }

  if (journalEntries.length === 0) {
    container.innerHTML = `
      <div class="profile-empty-state">
        <span class="profile-empty-icon">${icons.journal}</span>
        <p class="profile-empty-text">No journal entries yet</p>
        <p class="profile-empty-hint">Tap the journal icon on any verse to start writing</p>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="journal-list">
      ${journalEntries.map(entry => `
        <div class="journal-item" data-entry-id="${escapeHtml(entry.id)}">
          <div class="journal-item-header">
            <span class="journal-item-ref">${escapeHtml(entry.verseRef)}</span>
            <span class="journal-item-date">${escapeHtml(formatDate(entry.updatedAt || entry.createdAt))}</span>
          </div>
          <p class="journal-item-content">${escapeHtml(truncateText(entry.content, 100))}</p>
        </div>
      `).join('')}
    </div>
  `;

  // Handle click on journal item (open for editing)
  container.querySelectorAll('.journal-item').forEach(item => {
    item.addEventListener('click', () => {
      const entryId = item.dataset.entryId;
      const entry = journalEntries.find(e => e.id === entryId);
      if (entry) {
        // Keep profile open - journal editor will layer on top
        // When journal closes, user will see profile again
        showJournalEditor({ reference: entry.verseRef, text: entry.verseText }, entry);
      }
    });
  });
}

export function hideProfile() {
  if (!currentOverlay) return;

  // Always detach the ESC handler so it can't accumulate (#94).
  if (escKeyHandler) {
    document.removeEventListener('keydown', escKeyHandler);
    escKeyHandler = null;
  }

  currentOverlay.classList.remove('active');
  setTimeout(() => {
    currentOverlay?.remove();
    currentOverlay = null;
  }, 300);
}

export function applyTheme(themeKey) {
  const theme = THEMES[themeKey];
  if (!theme) return;

  document.documentElement.setAttribute('data-theme', themeKey);
}

function formatDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export default { showProfile, hideProfile, applyTheme };
